import React, { useState, useEffect } from 'react';
import { useCalendars } from '../../contexts/CalendarContext';
import Button from '../Common/Button';
import Input from '../Common/Input';
import { logCalendarJoined } from '../../services/analytics';

const JoinCalendarModal = ({ isOpen, onClose }) => {
    const { joinByCode } = useCalendars();

    const [code, setCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleClose = () => {
        setCode('');
        setError('');
        setSuccess('');
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!code.trim()) {
            setError('Please enter a share code');
            return;
        }

        setIsSubmitting(true);
        setError('');
        setSuccess('');

        try {
            const joinedCalendar = await joinByCode(code.trim().toUpperCase());
            setSuccess(`Successfully joined "${joinedCalendar.name}"!`);
            logCalendarJoined();
            setCode('');

            // Close modal after a short delay
            setTimeout(() => {
                handleClose();
            }, 1500);
        } catch (err) {
            setError(err.message || 'Failed to join calendar');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 overflow-y-auto animate-fade-in"
            role="dialog"
            aria-modal="true"
            aria-labelledby="join-modal-title"
        >
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={handleClose}
                aria-hidden="true"
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 animate-fade-in transition-colors duration-200"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button */}
                    <button
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        aria-label="Close modal"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Header */}
                    <div className="mb-6">
                        <h2 id="join-modal-title" className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            Join Calendar
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Enter a share code to join someone's calendar
                        </p>
                    </div>

                    {/* Error/Success Messages */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                        </div>
                    )}

                    {success && (
                        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                            <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
                        </div>
                    )}

                    {/* Join Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Input
                                label="Share Code"
                                id="code"
                                type="text"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                placeholder="Enter share code (e.g., ASD309SDFW0R3)"
                                disabled={isSubmitting}
                                maxLength={13}
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Enter the 13-character code provided by the calendar owner
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={handleClose}
                                disabled={isSubmitting}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting || !code.trim()}
                                className="flex-1"
                            >
                                {isSubmitting ? 'Joining...' : 'Join Calendar'}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default JoinCalendarModal;
